import { computed } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { setAllEntities, updateEntity, withEntities } from '@ngrx/signals/entities';
import { PumpSchedule } from 'libs/models';
import { JobProcedureTab } from '../job-procedure/components/job-procedure-tabs/job-procedure-tabs.store';

// ─── Interfaces ──────────────────────────────────────────────────────────────

export interface ShoeTrackData {
  shoeTrackLength: number | null;
  shoeTrackVolume: number | null;
  scheduledShutdown: string | null;
  targetSafetyFactor: string | null;
  batchMixingTime: string | null;
  spacerMixMethod: number;
  isManuallySpacerMixMethod: boolean;
  linerCirculationMethod: number | null;
  linerCementLength: number | null;
  isDirty: boolean;
}

export interface CpPumpTab {
  id: string;
  pumpScheduleId: string | null;
  index: number;
  shoeTrackData: ShoeTrackData;
}

export interface CpPumpState {
  selectedTabIndex: number;
  isInitialized: boolean;
}

// ─── Defaults ────────────────────────────────────────────────────────────────

export const DEFAULT_SHOE_TRACK_DATA: ShoeTrackData = {
  shoeTrackLength: null,
  shoeTrackVolume: null,
  scheduledShutdown: null,
  targetSafetyFactor: null,
  batchMixingTime: null,
  spacerMixMethod: 0,
  isManuallySpacerMixMethod: false,
  linerCirculationMethod: null,
  linerCementLength: null,
  isDirty: false,
};

const initialState: CpPumpState = {
  selectedTabIndex: 0,
  isInitialized: false,
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function extractShoeTrackFromPumpSchedule(ps: PumpSchedule | undefined): ShoeTrackData {
  if (!ps) {
    return { ...DEFAULT_SHOE_TRACK_DATA };
  }
  return {
    shoeTrackLength: ps.shoeTrackLength ?? null,
    shoeTrackVolume: ps.shoeTrackVolume ?? null,
    scheduledShutdown: ps.scheduledShutdown ?? null,
    targetSafetyFactor: ps.targetSafetyFactor ?? null,
    batchMixingTime: ps.batchMixingTime ?? null,
    spacerMixMethod: ps.spacerMixMethod ?? 0,
    isManuallySpacerMixMethod: ps.isManuallySpacerMixMethod ?? false,
    linerCirculationMethod: ps.linerCirculationMethod ?? null,
    linerCementLength: ps.linerCementLength ?? null,
    isDirty: false,
  };
}

function findMatchingPumpSchedule(
  tab: JobProcedureTab,
  pumpSchedules: PumpSchedule[]
): PumpSchedule | undefined {
  if (!pumpSchedules || pumpSchedules.length === 0) {
    return undefined;
  }
  // Match by pumpScheduleId first, then by order
  if (tab.pumpScheduleId) {
    const byId = pumpSchedules.find(ps => ps.pumpScheduleId === tab.pumpScheduleId);
    if (byId) return byId;
  }
  return pumpSchedules.find(ps => ps.order === tab.index);
}

// ─── Store ───────────────────────────────────────────────────────────────────

export const CpPumpStore = signalStore(
  withEntities<CpPumpTab>(),
  withState<CpPumpState>(initialState),

  withComputed(store => ({
    tabs: computed(() => store.entities()),

    selectedTab: computed(() =>
      store.entities().find(t => t.index === store.selectedTabIndex())
    ),

    selectedTabShoeTrack: computed(() => {
      const tab = store.entities().find(t => t.index === store.selectedTabIndex());
      return tab?.shoeTrackData ?? null;
    }),

    isAnyTabDirty: computed(() =>
      store.entities().some(t => t.shoeTrackData.isDirty)
    ),

    shoeTrackDataByOrder: computed(() => {
      const map = new Map<number, ShoeTrackData>();
      store.entities().forEach(t => {
        map.set(t.index, t.shoeTrackData);
      });
      return map;
    }),
  })),

  withMethods(store => ({
    initFromJobProcedureTabs(
      tabs: JobProcedureTab[],
      pumpSchedules: PumpSchedule[]
    ): void {
      const entities: CpPumpTab[] = tabs.map(tab => ({
        id: tab.id,
        pumpScheduleId: tab.pumpScheduleId,
        index: tab.index,
        shoeTrackData: extractShoeTrackFromPumpSchedule(
          findMatchingPumpSchedule(tab, pumpSchedules)
        ),
      }));

      patchState(store,
        setAllEntities(entities),
        {
          selectedTabIndex: 0,
          isInitialized: true,
        }
      );
    },

    initFromPumpSchedules(pumpSchedules: PumpSchedule[]): void {
      const entities: CpPumpTab[] = pumpSchedules.map((ps, index) => ({
        id: ps.pumpScheduleId,
        pumpScheduleId: ps.pumpScheduleId || null,
        index: ps.order ?? index,
        shoeTrackData: extractShoeTrackFromPumpSchedule(ps),
      }));

      patchState(store,
        setAllEntities(entities),
        {
          selectedTabIndex: store.selectedTabIndex(),
          isInitialized: true,
        }
      );
    },

    syncTabs(tabs: JobProcedureTab[]): void {
      const existingEntities = store.entities();
      const existingById = new Map(existingEntities.map(e => [e.id, e]));
      const existingByIndex = new Map(existingEntities.map(e => [e.index, e]));
      const referenceData = existingEntities.length > 0 ? existingEntities[0].shoeTrackData : null;

      const updatedEntities: CpPumpTab[] = tabs.map(tab => {
        const existing = existingById.get(tab.id) || existingByIndex.get(tab.index);
        if (existing) {
          // Preserve existing ShoeTrack data, update id/index
          return {
            ...existing,
            id: tab.id,
            index: tab.index,
            pumpScheduleId: tab.pumpScheduleId,
          };
        }
        // New tab — inherit general params from existing tabs
        return {
          id: tab.id,
          pumpScheduleId: tab.pumpScheduleId,
          index: tab.index,
          shoeTrackData: {
            ...DEFAULT_SHOE_TRACK_DATA,
            linerCirculationMethod: referenceData?.linerCirculationMethod ?? null,
            linerCementLength: referenceData?.linerCementLength ?? null,
            spacerMixMethod: referenceData?.spacerMixMethod ?? 0,
            isManuallySpacerMixMethod: referenceData?.isManuallySpacerMixMethod ?? false,
          },
        };
      });

      patchState(store, setAllEntities(updatedEntities));
    },

    selectTab(index: number): void {
      patchState(store, { selectedTabIndex: index });
    },

    updateTabShoeTrackData(tabId: string, partial: Partial<ShoeTrackData>): void {
      const entity = store.entities().find(e => e.id === tabId);
      if (!entity) return;

      patchState(store,
        updateEntity({
          id: tabId,
          changes: {
            shoeTrackData: {
              ...entity.shoeTrackData,
              ...partial,
              isDirty: true,
            },
          },
        })
      );
    },

    syncSpacerMixMethodAcrossAllTabs(method: number, isManually: boolean): void {
      const updated = store.entities().map(e => ({
        ...e,
        shoeTrackData: {
          ...e.shoeTrackData,
          spacerMixMethod: method,
          isManuallySpacerMixMethod: isManually,
        },
      }));
      patchState(store, setAllEntities(updated));
    },

    syncLinerParamsAcrossAllTabs(linerCirculationMethod: number | null, linerCementLength: number | null): void {
      const updated = store.entities().map(e => ({
        ...e,
        shoeTrackData: {
          ...e.shoeTrackData,
          linerCirculationMethod,
          linerCementLength,
        },
      }));
      patchState(store, setAllEntities(updated));
    },

    resetDirty(): void {
      const entities = store.entities();
      const resetEntities = entities.map(e => ({
        ...e,
        shoeTrackData: {
          ...e.shoeTrackData,
          isDirty: false,
        },
      }));
      patchState(store, setAllEntities(resetEntities));
    },
  }))
);
